import {
  Box,
  navTransitionDuration,
  navTransitionEasing,
  useTheme,
} from 'hephaestus/web';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type ContentWidth = 'lg' | number;

type ContentWidthRegistration = {
  id: number;
  width: ContentWidth;
};

type LayoutContextValue = {
  registerContentWidth: (width: ContentWidth) => number;
  unregisterContentWidth: (id: number) => void;
};

export const LayoutContext = createContext<LayoutContextValue | undefined>(
  undefined,
);

const LayoutWidthContext = createContext<ContentWidth | undefined>(undefined);

export const resolveContentWidth = (maxPageWidth?: number): ContentWidth =>
  !maxPageWidth || maxPageWidth === 1080 ? 'lg' : maxPageWidth;

let nextContentWidthId = 0;

type LayoutContentProp = {
  children: ReactNode;
};

type ContentContainerProp = {
  children: ReactNode;
  py?: string;
  transition?: boolean;
};

export const contentMaxWidth = (
  contentWidth: ContentWidth | undefined,
  lg: string,
) => {
  if (contentWidth === 'lg') return lg;
  if (typeof contentWidth === 'number') return `${contentWidth}px`;
  return '100%';
};

export const ContentContainer = ({
  children,
  py,
  transition = false,
}: ContentContainerProp) => {
  const theme = useTheme();
  const contentWidth = useContext(LayoutWidthContext);

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        width: '100%',
        minWidth: 0,
        py,
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: contentMaxWidth(contentWidth, theme.breakpoint.lg),
        ...(transition && {
          transition: `max-width ${navTransitionDuration}ms ${navTransitionEasing}`,
        }),
      }}
    >
      {children}
    </Box>
  );
};

export const LayoutContent = ({ children }: LayoutContentProp) => {
  const [registrations, setRegistrations] = useState<
    ContentWidthRegistration[]
  >([]);
  const registerContentWidth = useCallback((width: ContentWidth) => {
    const id = ++nextContentWidthId;
    setRegistrations((current) => [...current, { id, width }]);
    return id;
  }, []);
  const unregisterContentWidth = useCallback((id: number) => {
    setRegistrations((current) =>
      current.filter((registration) => registration.id !== id),
    );
  }, []);
  const value = useMemo(
    () => ({ registerContentWidth, unregisterContentWidth }),
    [registerContentWidth, unregisterContentWidth],
  );
  const contentWidth = registrations.at(-1)?.width;

  return (
    <LayoutContext.Provider value={value}>
      <LayoutWidthContext.Provider value={contentWidth}>
        {children}
      </LayoutWidthContext.Provider>
    </LayoutContext.Provider>
  );
};
